import { BoxWithAdornment } from "@/components/atoms/BoxWithAdornment";
import { Icon } from "@/components/atoms/Icon";
import { Typography, typographyStyles } from "@/components/ui/typography";
import React, { memo, useRef } from "react";
import { CopyIcon } from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const REMARK_PLUGINS = [remarkGfm];

const MARKDOWN_TABLE_MAX_HEIGHT = 320;

// Colours come from ``theme.vars``, not the palette's values. The element map
// is built once per instance (see ``MarkdownInner``) and the palette holds the
// values of the scheme it was built in, so bold text and tables kept that
// scheme after the theme switched. The variables switch with it.
function markdownTableStyles() {
  const border = `1px solid ${"var(--border)"}`;

  const cellStyle: React.CSSProperties = {
    padding: "6px 8px",
    fontSize: typographyStyles["body-sm"]
      .fontSize as React.CSSProperties["fontSize"],
    lineHeight: typographyStyles["body-sm"]
      .lineHeight as React.CSSProperties["lineHeight"],
    borderBottom: border,
    borderRight: border,
    verticalAlign: "top",
    color: "var(--muted-foreground)",
  };

  return {
    scroll: {
      display: "block",
      width: "100%",
      maxWidth: "100%",
      minHeight: 0,
      maxHeight: MARKDOWN_TABLE_MAX_HEIGHT,
      overflow: "auto",
      my: 2,
      borderRadius: "calc(var(--radius) - 4px)",
      outline: border,
      outlineOffset: "-1px",
    },
    table: {
      borderCollapse: "collapse" as const,
      tableLayout: "auto" as const,
      width: "max-content",
      minWidth: "100%",
    },
    th: {
      ...cellStyle,
      fontWeight: 500,
      fontSize: typographyStyles["body-xs"]
        .fontSize as React.CSSProperties["fontSize"],
      textTransform: "uppercase" as const,
      color: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
      backgroundColor: "var(--muted)",
      position: "sticky" as const,
      top: 0,
      zIndex: 1,
    },
    td: cellStyle,
  };
}

// Typography scaling mappings
const BODY_HIERARCHY = ["body-xs", "body-sm", "body-md", "body-lg"] as const;
const TITLE_HIERARCHY = ["title-sm", "title-md", "title-lg"] as const;
const MONO_BODY_HIERARCHY = [
  "mono-body-sm",
  "mono-body-md",
  "mono-body-lg",
  "mono-body-xl",
] as const;
const MONO_TITLE_HIERARCHY = [
  "mono-title-sm",
  "mono-title-md",
  "mono-title-lg",
  "mono-title-xl",
] as const;
const HEADING_HIERARCHY = ["h4", "h3", "h2", "h1"] as const;

type TypographyLevel =
  | (typeof BODY_HIERARCHY)[number]
  | (typeof TITLE_HIERARCHY)[number]
  | (typeof MONO_BODY_HIERARCHY)[number]
  | (typeof MONO_TITLE_HIERARCHY)[number]
  | (typeof HEADING_HIERARCHY)[number];

function scaleTypographyLevel(
  currentLevel: TypographyLevel,
  resize: number,
): TypographyLevel {
  // Find which hierarchy the current level belongs to
  let hierarchy: readonly string[];
  let currentIndex: number;

  if (BODY_HIERARCHY.includes(currentLevel as any)) {
    hierarchy = BODY_HIERARCHY;
    currentIndex = BODY_HIERARCHY.indexOf(currentLevel as any);
  } else if (TITLE_HIERARCHY.includes(currentLevel as any)) {
    hierarchy = TITLE_HIERARCHY;
    currentIndex = TITLE_HIERARCHY.indexOf(currentLevel as any);
  } else if (MONO_BODY_HIERARCHY.includes(currentLevel as any)) {
    hierarchy = MONO_BODY_HIERARCHY;
    currentIndex = MONO_BODY_HIERARCHY.indexOf(currentLevel as any);
  } else if (MONO_TITLE_HIERARCHY.includes(currentLevel as any)) {
    hierarchy = MONO_TITLE_HIERARCHY;
    currentIndex = MONO_TITLE_HIERARCHY.indexOf(currentLevel as any);
  } else if (HEADING_HIERARCHY.includes(currentLevel as any)) {
    hierarchy = HEADING_HIERARCHY;
    currentIndex = HEADING_HIERARCHY.indexOf(currentLevel as any);
  } else {
    // If not found in any hierarchy, return the original level
    return currentLevel;
  }

  // Apply the resize offset (negative resize = smaller text, positive = larger text)
  const newIndex = Math.max(
    0,
    Math.min(hierarchy.length - 1, currentIndex + resize),
  );
  return hierarchy[newIndex] as TypographyLevel;
}

function createMarkdownComponents(
  style: React.CSSProperties | undefined,
  resize: number,
): Components {
  const tableSx = markdownTableStyles();

  return {
    strong: ({ children }) => (
      <span
        style={{
          color: "var(--foreground)",
          fontFamily: "Inter-SemiBold",
        }}
      >
        {children}
      </span>
    ),
    ul: ({ children }) => (
      <ul className="pl-6 mb-2 mt-1 pt-0 pb-0 list-disc">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="pl-6 mb-2 mt-1 pt-0 pb-0 list-decimal">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="list-item mb-0.5 text-inherit">{children}</li>
    ),
    hr: ({ children }) => (
      <hr className="mt-4 mb-4 h-px [border:none] w-full bg-border" />
    ),
    table: ({ children }) => (
      <div className="block w-full max-w-full min-h-0 max-h-80 overflow-auto mt-4 mb-4 rounded-sm [outline:1px_solid_var(--border)] [outline-offset:-1px]">
        <table style={tableSx.table}>{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead>{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => <th style={tableSx.th}>{children}</th>,
    td: ({ children }) => <td style={tableSx.td}>{children}</td>,
    body: ({ children }) => (
      <Typography
        component="body"
        level={scaleTypographyLevel("body-md", resize)}
        style={style}
      >
        {children}
      </Typography>
    ),
    text: ({ children }) => (
      <Typography
        component="text"
        level={scaleTypographyLevel("body-md", resize)}
        style={style}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography
        component="p"
        level={scaleTypographyLevel("body-md", resize)}
        style={style}
        className="mb-2 mt-0 last:mb-0"
      >
        {children}
      </Typography>
    ),
    blockquote: ({ children }) => (
      <Typography
        component="blockquote"
        level={scaleTypographyLevel("body-md", resize)}
        style={style}
        // pl: 2,
        // ml: 2,
        className="[border-left-width:4px] [border-left-style:solid] border-neutral-400 mb-4 italic"
      >
        {children}
      </Typography>
    ),
    code: ({ children }) => {
      // if multiple lines, return a code block
      const codeString =
        typeof children === "string"
          ? children
          : Array.isArray(children)
            ? children.join("")
            : String(children);
      const isMultipleLines = codeString.split("\n").length > 1;

      if (isMultipleLines) {
        return (
          <Typography
            component="code"
            level={scaleTypographyLevel("body-sm", resize)}
            style={style}
            className="font-mono p-1 rounded-[1px]"
          >
            {children}
          </Typography>
        );
      }
      return (
        <div className="[border-width:1px] [border-style:solid] border-border rounded pl-1 pr-1 w-min shadow-sm inline-block items-center justify-center">
          <BoxWithAdornment
            endAdornment={
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigator.clipboard.writeText(codeString);
                }}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon ReactIcon={CopyIcon} />
              </div>
            }
            fontKey="mono-body-md"
          >
            {children}
          </BoxWithAdornment>
        </div>
      );
    },
    h1: ({ children }) => (
      <Typography
        component="h1"
        level={scaleTypographyLevel("h1", resize)}
        // style={style}
        className="mb-3 mt-5 font-bold"
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        component="h2"
        level={scaleTypographyLevel("h2", resize)}
        // style={style}
        className="mb-2 mt-4 text-lg font-semibold"
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        component="h3"
        level={scaleTypographyLevel("h3", resize)}
        // style={style}
        className="mb-1 mt-3 text-sm font-semibold"
      >
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography
        component="h4"
        level={scaleTypographyLevel("h4", resize)}
        // style={style}
        className="mb-2 mt-4 font-medium"
      >
        {children}
      </Typography>
    ),
  };
}

function MarkdownInner({
  children,
  style,
  resize = 0,
}: {
  children: string;
  style?: React.CSSProperties;
  resize?: number;
}) {
  // Bind the components map to the instance: create once, never replace.
  // ReactMarkdown treats new component-function identities as new component
  // types and remounts the rendered DOM (including <table>), which would
  // reset scroll position on every parent re-render.
  const componentsRef = useRef<Components | null>(null);
  if (componentsRef.current === null) {
    componentsRef.current = createMarkdownComponents(style, resize);
  }
  const components = componentsRef.current;

  return (
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
      {children}
    </ReactMarkdown>
  );
}

export const Markdown = memo(MarkdownInner);
